import { BankingDetailsCard } from '@dealstack/frontend/investor/pages/Valuations/ValuationsDrawer/BankingDetailsCard';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Box, Divider, Text } from '@mantine/core';
import { getExtraIdentifierRows } from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import type { BankingDetailsComponentProps } from './BankingDetailsComponent.types';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';

export const BankingDetailsComponent = ({
	name,
	bankName,
	accNumber,
	iban,
	swift,
	identifiers,
	countryCode,
	currency,
	instructions,
}: BankingDetailsComponentProps) => {
	const tInvestor = useTranslationInvestor();

	const hasIban = !!iban.trim();
	const identifierRows = getExtraIdentifierRows(identifiers);
	const countryName = countryCode ? getCountryNameByCode(countryCode) : '';

	return (
		<Box
			style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column', width: '100%' }}
			className={REPLAY_MASK_CLASS}
			data-sentry-mask
		>
			<Text mb="0.5rem" size="B6Medium">
				{tInvestor('valuations.drawer.banking_details.description')}
			</Text>
			<BankingDetailsCard
				title={tInvestor('valuations.drawer.banking_details.account_holder')}
				value={name || LONG_DASH}
			/>
			<BankingDetailsCard
				title={tInvestor('valuations.drawer.banking_details.bank_name')}
				value={bankName || LONG_DASH}
			/>
			{accNumber ? (
				<BankingDetailsCard
					title={tInvestor('valuations.drawer.banking_details.account_number')}
					value={accNumber}
				/>
			) : null}
			{hasIban ? (
				<BankingDetailsCard
					title={tInvestor('valuations.drawer.banking_details.iban')}
					value={iban}
				/>
			) : null}
			{swift ? (
				<BankingDetailsCard
					title={tInvestor('valuations.drawer.banking_details.swift')}
					value={swift}
				/>
			) : null}
			{identifierRows.map((row) => (
				<BankingDetailsCard key={row.key} title={row.label} value={row.value} />
			))}
			{countryName ? (
				<BankingDetailsCard
					title={tInvestor('valuations.drawer.banking_details.country', {
						defaultValue: 'Country',
					})}
					value={countryName}
				/>
			) : null}
			{currency ? (
				<BankingDetailsCard
					title={tInvestor('valuations.drawer.banking_details.currency', {
						defaultValue: 'Currency',
					})}
					value={currency}
				/>
			) : null}
			{instructions ? (
				<Box
					sx={(theme) => ({
						borderRadius: '0.25rem 0.25rem 0rem 0rem',
						border: `1px solid ${theme.colors.gray[4]}`,
						boxShadow: '0px 4px 8px 0px #B8BFC2',
						padding: '1rem',
						marginTop: '1rem',
					})}
				>
					<Text size="B6Bold">
						{tInvestor('valuations.drawer.banking_details.instructions_title')}
					</Text>

					<Divider mt="1rem" mb="1rem" />
					<Text size="B6Medium">{instructions}</Text>
				</Box>
			) : null}
		</Box>
	);
};
